'use client';

import { c as _c } from "react/compiler-runtime";
import Link, { type LinkProps } from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '~/components/primitives';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '~/modules/trpc/client';
import { useCartCtx } from './context';
import { useI18n } from '~/features/i18n';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '~/components/ui/hover-card';
import { HeaderHovercard, HeaderHovercardTitle, HeaderHovercardList, HeaderHovercardCTA, HeaderHovercardEmpty } from './ui/header-hovercard';
import { CartItemCard } from './CartItemCard';
import { I18N_CONF } from '@funkey/locales/config';
import { usePortalCtx } from '~/features/global/portal';
import { cartHovercardEvent } from './cartHovercardEvents';
const AUTOCLOSE_DELAY = 3000;
const ShoppingCart: React.FC<{
  cartURL: LinkProps['href'];
}> = props => {
  const $ = _c(30);
  const trpc = useTRPC();
  const cart = useCartCtx();
  const i18n = useI18n();
  const portal = usePortalCtx();
  const [open, setOpen] = useState(false);
  const autoCloseRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const clearAutoClose = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => cartHovercardEvent.subscribe(() => {
      setOpen(true);
      clearAutoClose();
      autoCloseRef.current = setTimeout(() => {
        setOpen(false);
      }, AUTOCLOSE_DELAY);
    });
    t2 = [clearAutoClose];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => clearAutoClose();
    t4 = [clearAutoClose];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = nextOpen => {
      setOpen(nextOpen);
      if (nextOpen) {
        clearAutoClose();
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[6] !== cart.id || $[7] !== trpc.cart.items.count) {
    t6 = trpc.cart.items.count.queryOptions(undefined, {
      enabled: cart.id !== null
    });
    $[6] = cart.id;
    $[7] = trpc.cart.items.count;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const countQuery = useQuery(t6);
  let t7;
  if ($[9] !== cart.id || $[10] !== trpc.cart.get) {
    t7 = trpc.cart.get.queryOptions(undefined, {
      enabled: cart.id !== null
    });
    $[9] = cart.id;
    $[10] = trpc.cart.get;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const cartQuery = useQuery(t7);
  const hasItems = cart.id && !!countQuery.data;
  let t8;
  if ($[12] !== countQuery || $[13] !== hasItems) {
    t8 = hasItems && <div className="absolute -top-1 left-4"><p className="flex h-1 w-1 items-center justify-center rounded-full bg-orange p-2 text-xs font-bold text-white">{countQuery.data}</p></div>;
    $[12] = countQuery;
    $[13] = hasItems;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon name="bag" />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== props.cartURL || $[17] !== t8) {
    t10 = <HoverCardTrigger asChild={true}><Link href={props.cartURL} className="relative block" data-testid="header-cart-link">{t8}{t9}</Link></HoverCardTrigger>;
    $[16] = props.cartURL;
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== cartQuery || $[20] !== countQuery || $[21] !== hasItems || $[22] !== i18n || $[23] !== portal || $[24] !== props.cartURL) {
    t11 = <HoverCardContent align="center" sideOffset={16} showArrow={true} className="w-80 origin-top">{hasItems ? <HeaderHovercard><HeaderHovercardTitle href={props.cartURL}>{i18n._("cart.dropdown.title", undefined, {
            message: "Shopping cart"
          })}</HeaderHovercardTitle>{cartQuery.isLoading ? <div className="space-y-3">{Array.from({
            length: countQuery.data
          }).map(_temp)}</div> : <HeaderHovercardList>{cartQuery.data?.items.map(_temp2)}</HeaderHovercardList>}<HeaderHovercardCTA href={props.cartURL}>{i18n._("cart.dropdown.view-all", undefined, {
            message: "View cart"
          })}</HeaderHovercardCTA></HeaderHovercard> : <HeaderHovercardEmpty icon="bag" message={i18n._("cart.dropdown.empty", undefined, {
        message: "Your cart is empty"
      })} ctaLabel={i18n._("cart.dropdown.start-adding", undefined, {
        message: "Start adding teambuildings"
      })} ctaHref={portal.portal === "BIZZ" ? `${portal.baseURLPath}/booking` : I18N_CONF.getTranslatedUrl("teambuildings", {
        locale: i18n.locale
      })} />}</HoverCardContent>;
    $[19] = cartQuery;
    $[20] = countQuery;
    $[21] = hasItems;
    $[22] = i18n;
    $[23] = portal;
    $[24] = props.cartURL;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== open || $[27] !== t10 || $[28] !== t11) {
    t12 = <HoverCard open={open} onOpenChange={handleOpenChange} openDelay={200} closeDelay={150}>{t10}{t11}</HoverCard>;
    $[26] = open;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
export default ShoppingCart;
function _temp(_, idx) {
  return <div key={idx} className="flex animate-pulse items-center gap-3"><div className="h-12 w-16 rounded-md bg-gray-200" /><div className="h-4 flex-1 rounded bg-gray-200" /></div>;
}
function _temp2(item) {
  return <CartItemCard key={item.id} item={item} size="sm" />;
}